import { Auth_cleanse } from './Auth.cleanse'
import { Devi_saveInProgressData } from '../Devi/Devi.InProgressData'
import { DS_getData, DS_setData } from '../DS/DS.Data'
import { Noti_unsubscribe } from '../Noti/Noti.unsubscribe'
import { User_isLoggedIn } from '../User/User.get'
import { Devi_saveCompletedData } from '../Devi/Devi.CompletedData'
import { updateLastActiveTime } from '../Devi/Devi.recentUsers'
import { getAuthUrl } from '../Config'
import { createClient } from '../../../server/client/client.no-deps'
import { Serv_httpRequestWithOption } from '../Serv/Serv.httpRequest'
import type { Cust_ICustomer } from '../Cust/Cust.ICustomer'
import * as ZendeskWebChat from '../../../app-core/lib/util/zendeskWebChat'

function deleteAuthSession(accessToken: string) {
    // We don't allow cross origin requests on the auth server so this is a pretty
    // dirty hack to just fire a request at the auth server. We don't care about
    // the response, it is just a fire and forget rpc
    return new Promise<void>((resolve) => {
        const img = document.createElement('img')
        const cleanup = () => {
            img.remove()
            resolve()
        }
        img.onload = cleanup
        // since it isn't actually an image returned the browser will raise an error
        // but that's okay since we don't care about the response
        img.onerror = cleanup

        img.src = `${getAuthUrl()}/auth/logout?token=${accessToken}`
        document.body.appendChild(img)
    })
}

export async function Auth_logout() {
    await Noti_unsubscribe()

    const { accessToken, user, customer } = DS_getData()

    await unsubscribeFromWebPushNotifications(accessToken, customer)

    await updateLastActiveTime(user)

    // Clearout all the data
    await Auth_cleanse()

    // Delete the token on the auth server
    await deleteAuthSession(accessToken)

    // clear Zendesk Chat cookies
    ZendeskWebChat.clearCookies()

    // Update the auth state, this will navigate to the user select page
    DS_setData('LoggedOut', () => ({ auth: { status: 'ready' } }))
}

export async function unsubscribeFromWebPushNotifications(accessToken: string, customer: Cust_ICustomer) {
    try {
        const registration = await navigator.serviceWorker?.getRegistration()
        const subscription = await registration?.pushManager.getSubscription()

        if (!subscription) {
            return
        }

        const endpoint = subscription.endpoint
        subscription.unsubscribe()

        const httpClient = createClient(Serv_httpRequestWithOption({ accessToken, customer }))
        await httpClient.removeSubscription(endpoint)
    } catch (ignore) {
        // Swallow errors
    }
}

export async function Auth_safeLogout() {
    try {
        const { safeLogout, user } = DS_getData()

        // Do not try to do twice
        if (safeLogout.status !== 'ready') return

        // Do not bother if not logged in
        if (!User_isLoggedIn()) return

        // Show spinner
        DS_setData('SafeLogoutInitiated', () => ({ safeLogout: { status: 'in-progress' } }))

        // Save data
        await Devi_saveInProgressData()
        await Devi_saveCompletedData()

        await updateLastActiveTime(user)

        // Logout
        await Noti_unsubscribe()
        await Auth_cleanse()

        // clear Zendesk Chat cookies
        ZendeskWebChat.clearCookies()

        // Hide the spinner and show the user select screen
        DS_setData('SafeLogoutSuccess', ({ auth }) => ({
            safeLogout: { status: 'ready' },
            auth: auth.status === 'pending-automatic-login' ? auth : { status: 'ready' },
        }))
    } catch (error) {
        // Show an error screen
        DS_setData('SafeLogoutFailed', () => ({ safeLogout: { status: 'failure', error } }))
    }
}
